import React, { useState, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  RefreshControl,
  Alert,
  ScrollView,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { notificationsApi, changeRequestsApi } from '../../services/apiService';
import { NotificationList, ChangeRequest } from '../../types';
import ChangeRequestApprovalModal from '../../components/ChangeRequestApprovalModal';
import { COLORS, FONTS } from '../../constants/config';
import { NotificationsSkeleton, EmptyState, PageContainer, Navbar, Breadcrumb, SKELETON_DIMENSIONS } from '../../components';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { useResponsive } from '../../hooks';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNotificationsQuery, useMarkNotificationReadMutation, useMarkAllNotificationsReadMutation } from '../../hooks/queries';

type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// Category definitions for filter tabs
type NotificationCategory = 'all' | 'goals' | 'initiatives' | 'kpis' | 'approvals' | 'comments' | 'system';

interface CategoryConfig {
  id: NotificationCategory;
  label: string;
  icon: keyof typeof Ionicons.glyphMap;
  types: string[]; // notification_type values that belong to this category
}

const NOTIFICATION_CATEGORIES_CONFIG: Omit<CategoryConfig, 'label'>[] = [
  { id: 'system', icon: 'settings-outline', types: ['system'] },
  { id: 'comments', icon: 'chatbubble-outline', types: ['comment', 'comment_reply', 'comment_mention'] },
  { id: 'approvals', icon: 'checkmark-done-outline', types: ['change_request', 'change_approved', 'change_rejected'] },
  { id: 'kpis', icon: 'analytics-outline', types: ['kpi_due', 'kpi_overdue'] },
  { id: 'initiatives', icon: 'rocket-outline', types: ['initiative_assigned', 'initiative_submitted', 'initiative_approved', 'initiative_rejected', 'initiative_update'] },
  { id: 'goals', icon: 'flag-outline', types: ['goal_assigned', 'goal_submitted', 'goal_approved', 'goal_rejected', 'goal_deadline', 'goal_deleted', 'goal_edited'] },
  { id: 'all', icon: 'notifications-outline', types: [] },
];

const NOTIFICATION_ICONS: Record<string, keyof typeof Ionicons.glyphMap> = {
  // KPI notifications
  kpi_due: 'time-outline',
  kpi_overdue: 'warning-outline',
  // Goal notifications
  goal_assigned: 'flag-outline',
  goal_submitted: 'paper-plane-outline',
  goal_approved: 'checkmark-circle-outline',
  goal_rejected: 'close-circle-outline',
  goal_deadline: 'alarm-outline',
  goal_deleted: 'trash-outline',
  goal_edited: 'create-outline',
  // Initiative notifications
  initiative_assigned: 'rocket-outline',
  initiative_submitted: 'paper-plane-outline',
  initiative_approved: 'checkmark-circle-outline',
  initiative_rejected: 'close-circle-outline',
  initiative_update: 'sync-outline',
  // Change request notifications
  change_request: 'document-text-outline',
  change_approved: 'checkmark-done-outline',
  change_rejected: 'ban-outline',
  // System notifications
  system: 'notifications-outline',
};

const NOTIFICATION_COLORS: Record<string, string> = {
  // KPI notifications
  kpi_due: '#EAB308',  // Yellow
  kpi_overdue: '#EF4444',  // Red
  // Goal notifications - pending (yellow)
  goal_assigned: '#3B82F6',  // Blue
  goal_submitted: '#EAB308',  // Yellow
  goal_approved: '#22C55E',  // Green
  goal_rejected: '#EF4444',  // Red
  goal_deadline: '#EAB308',  // Yellow
  goal_deleted: '#EF4444',  // Red
  goal_edited: '#3B82F6',  // Blue
  // Initiative notifications
  initiative_assigned: '#3B82F6',  // Blue
  initiative_submitted: '#EAB308',  // Yellow
  initiative_approved: '#22C55E',  // Green
  initiative_rejected: '#EF4444',  // Red
  initiative_update: '#64748B',  // Gray
  // Change request notifications
  change_request: '#EAB308',  // Yellow
  change_approved: '#22C55E',  // Green
  change_rejected: '#EF4444',  // Red
  // System notifications
  system: '#A9211F',  // Brand primary
};

export default function NotificationsScreen() {
  const navigation = useNavigation<NavigationProp>();
  const { isMobile, isSmallMobile, containerPadding } = useResponsive();
  const { user } = useAuth();
  const { colors, isDark } = useTheme();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<NotificationCategory>('all');

  // Change-request approval modal (opened when a change_request notification is tapped)
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedChangeRequest, setSelectedChangeRequest] = useState<ChangeRequest | null>(null);
  const [loadingChangeRequest, setLoadingChangeRequest] = useState(false);

  // Build categories with translated labels
  const NOTIFICATION_CATEGORIES: CategoryConfig[] = useMemo(() =>
    NOTIFICATION_CATEGORIES_CONFIG.map(cat => ({
      ...cat,
      label: cat.id === 'all' ? t('common.all') : t(`notifications.categories.${cat.id}`),
    })),
    [t]
  );

  // Theme colors for dark mode
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textSecondary: colors.textSecondary,
    textMuted: colors.textMuted,
    iconDefault: colors.iconDefault,
    border: colors.border,
    backgroundAlt: colors.backgroundAlt,
    sectionBg: isDark ? colors.backgroundAlt : '#F1F5F9',
  };

  // React Query data fetching
  const {
    data: notificationsData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useNotificationsQuery(user?.id);

  const notifications = notificationsData?.results || [];

  // Filter notifications by selected category
  const filteredNotifications = useMemo(() => {
    if (selectedCategory === 'all') {
      return notifications;
    }
    const category = NOTIFICATION_CATEGORIES.find(c => c.id === selectedCategory);
    if (!category) return notifications;
    return notifications.filter((n: NotificationList) => category.types.includes(n.notification_type));
  }, [notifications, selectedCategory, NOTIFICATION_CATEGORIES]);

  // Calculate unread count per category
  const categoryUnreadCounts = useMemo(() => {
    const counts: Record<NotificationCategory, number> = {
      all: 0,
      goals: 0,
      initiatives: 0,
      kpis: 0,
      approvals: 0,
      comments: 0,
      system: 0,
    };

    notifications.forEach((n: NotificationList) => {
      if (!n.is_read) {
        counts.all++;
        // Find which category this notification belongs to
        for (const category of NOTIFICATION_CATEGORIES) {
          if (category.id !== 'all' && category.types.includes(n.notification_type)) {
            counts[category.id]++;
            break;
          }
        }
      }
    });

    return counts;
  }, [notifications, NOTIFICATION_CATEGORIES]);

  const markReadMutation = useMarkNotificationReadMutation();
  const markAllReadMutation = useMarkAllNotificationsReadMutation();


  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await refetch();
    setRefreshing(false);
  }, [refetch]);

  const handleNotificationPress = async (notification: NotificationList) => {
    // Mark as read first
    if (!notification.is_read) {
      try {
        await notificationsApi.markRead(notification.id);
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }

    // Navigate to related item
    navigateToRelatedItem(notification);
  };

  const navigateToRelatedItem = async (notification: NotificationList) => {
    const { related_object_type, related_object_id } = notification;

    // Debug: Log notification details
    console.log('=== Navigation Debug ===');
    console.log('Notification ID:', notification.id);
    console.log('related_object_type:', related_object_type);
    console.log('related_object_id:', related_object_id);

    // Helper function to navigate based on user role
    const navigateToGoal = (goalId: number) => {
      if (user?.position_type === 'EMPLOYEE') {
        navigation.navigate('GoalsStack' as any, {
          screen: 'GoalDetail',
          params: { id: goalId },
        });
      } else {
        navigation.navigate('StrategyStack' as any, {
          screen: 'GoalDetail',
          params: { id: goalId },
        });
      }
    };

    const navigateToInitiative = (initiativeId: number) => {
      navigation.navigate('InitiativesStack' as any, {
        screen: 'InitiativeDetail',
        params: { id: initiativeId },
      });
    };

    const navigateToKPI = (kpiId: number) => {
      navigation.navigate('GoalsStack' as any, {
        screen: 'KPIDetail',
        params: { id: kpiId },
      });
    };

    // First try using related_object_type and related_object_id
    if (related_object_type && related_object_id) {
      switch (related_object_type) {
        case 'goal':
          navigateToGoal(related_object_id);
          return;

        case 'initiative':
          navigateToInitiative(related_object_id);
          return;

        case 'kpi':
          navigateToKPI(related_object_id);
          return;

        case 'change_request':
          // Open approval modal for change requests
          try {
            setLoadingChangeRequest(true);
            const changeRequest = await changeRequestsApi.get(related_object_id);
            setSelectedChangeRequest(changeRequest);
            setShowApprovalModal(true);
          } catch (err: any) {
            console.error('Error fetching change request:', err);
            Alert.alert(t('common.error'), t('notifications.failedToLoadRequest'));
          } finally {
            setLoadingChangeRequest(false);
          }
          return;

        default:
          console.log('Unknown related_object_type:', related_object_type);
      }
    }

    // Fallback: Parse the link field for older notifications
    const link = notification.link;
    if (link) {
      // Parse link like /goals/5 or /initiatives/10
      const goalMatch = link.match(/\/goals\/(\d+)/);
      if (goalMatch) {
        navigateToGoal(parseInt(goalMatch[1], 10));
        return;
      }

      const initiativeMatch = link.match(/\/initiatives\/(\d+)/);
      if (initiativeMatch) {
        navigateToInitiative(parseInt(initiativeMatch[1], 10));
        return;
      }

      const kpiMatch = link.match(/\/kpis\/(\d+)/);
      if (kpiMatch) {
        navigateToKPI(parseInt(kpiMatch[1], 10));
        return;
      }
    }

    console.log('No navigation target found for this notification');
  };


  const handleMarkAllRead = useCallback(async () => {
    try {
      await notificationsApi.markAllRead();
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  }, [queryClient]);

  const renderItem = ({ item }: { item: NotificationList }) => {
    const iconColor = NOTIFICATION_COLORS[item.notification_type] || COLORS.primary;
    const iconBgColor = iconColor + '15';

    return (
      <TouchableOpacity
        style={[
          styles.card,
          { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder },
          !item.is_read && [styles.cardUnread, { backgroundColor: isDark ? '#422006' : '#FFFBEB', borderColor: isDark ? '#78350F' : '#FDE68A' }],
          { minHeight: SKELETON_DIMENSIONS.NOTIFICATION_ITEM_HEIGHT }
        ]}
        onPress={() => handleNotificationPress(item)}
        activeOpacity={0.7}
      >
        {/* Icon */}
        <View style={[styles.iconContainer, { backgroundColor: iconBgColor }]}>
          <Ionicons
            name={NOTIFICATION_ICONS[item.notification_type] || 'notifications-outline'}
            size={22}
            color={iconColor}
          />
        </View>

        {/* Content */}
        <View style={styles.content}>
          {/* Title */}
          <Text style={[styles.title, { color: themeColors.textSecondary }, !item.is_read && [styles.titleUnread, { color: themeColors.textPrimary }]]}>
            {item.title}
          </Text>

          {/* Message */}
          {item.message && (
            <Text style={[styles.message, { color: themeColors.textMuted }]} numberOfLines={2}>
              {item.message}
            </Text>
          )}

          {/* Meta row: Organization & Time */}
          <View style={styles.metaRow}>
            {item.department_name && (
              <View style={[styles.orgBadge, { backgroundColor: isDark ? '#451A1A' : '#FEF2F2' }]}>
                <Text style={styles.orgText}>{item.department_name}</Text>
              </View>
            )}
            <Text style={[styles.time, { color: themeColors.textMuted }]}>
              {item.time_ago || item.notification_type_display}
            </Text>
          </View>
        </View>

        {/* Unread indicator */}
        {!item.is_read && <View style={styles.unreadDot} />}

        {/* Navigation arrow */}
        {item.related_object_type && item.related_object_id && (
          <Ionicons
            name="chevron-back-outline"
            size={18}
            color={themeColors.textMuted}
            style={styles.arrow}
          />
        )}
      </TouchableOpacity>
    );
  };

  if (isLoading) {
    return (
      <PageContainer>
        <Navbar title={t('notifications.title')} />
        <NotificationsSkeleton />
      </PageContainer>
    );
  }

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  // Get current category config for empty state
  const currentCategoryConfig = NOTIFICATION_CATEGORIES.find(c => c.id === selectedCategory);

  // Get category-specific empty state message
  const getEmptyStateMessage = () => {
    switch (selectedCategory) {
      case 'goals':
        return t('notifications.noGoalsNotifications');
      case 'initiatives':
        return t('notifications.noInitiativesNotifications');
      case 'kpis':
        return t('notifications.noKpisNotifications');
      case 'approvals':
        return t('notifications.noApprovalsNotifications');
      case 'comments':
        return t('notifications.noCommentsNotifications');
      case 'system':
        return t('notifications.noSystemNotifications');
      default:
        return t('notifications.newNotificationsWillAppear');
    }
  };

  return (
    <>
      <PageContainer>
        <Navbar title={t('notifications.title')} />
        {/* Breadcrumb with sidebar toggle */}
        <Breadcrumb
          items={[
            { label: t('breadcrumb.home') },
            { label: t('notifications.title') },
          ]}
        />

        {/* Header with mark all read */}
        {unreadCount > 0 && (
          <View style={[styles.header, { backgroundColor: themeColors.card, borderBottomColor: themeColors.border }]}>
            <TouchableOpacity style={[styles.markAllButton, { backgroundColor: isDark ? '#451A1A' : '#FEF2F2' }]} onPress={handleMarkAllRead}>
              <Ionicons name="checkmark-done-outline" size={18} color="#A9211F" />
              <Text style={styles.markAllText}>{t('notifications.markAllRead')} ({unreadCount})</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Category Filter Tabs */}
        <View style={[styles.categoryTabsContainer, { backgroundColor: themeColors.card, borderBottomColor: themeColors.border }]}>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.categoryTabsScroll}
            // RTL: Start from right side
            style={{ direction: 'rtl' }}
          >
            {NOTIFICATION_CATEGORIES.map((category) => {
              const isActive = selectedCategory === category.id;
              const unreadInCategory = categoryUnreadCounts[category.id];

              return (
                <TouchableOpacity
                  key={category.id}
                  style={[
                    styles.categoryTab,
                    { backgroundColor: isDark ? '#374151' : '#F3F4F6' },
                    isActive && styles.categoryTabActive,
                  ]}
                  onPress={() => setSelectedCategory(category.id)}
                  activeOpacity={0.7}
                >
                  <Ionicons
                    name={category.icon}
                    size={18}
                    color={isActive ? '#FFFFFF' : themeColors.textMuted}
                    style={styles.categoryTabIcon}
                  />
                  <Text
                    style={[
                      styles.categoryTabText,
                      { color: themeColors.textMuted },
                      isActive && styles.categoryTabTextActive,
                    ]}
                  >
                    {category.label}
                  </Text>
                  {unreadInCategory > 0 && (
                    <View
                      style={[
                        styles.categoryBadge,
                        isActive && styles.categoryBadgeActive,
                      ]}
                    >
                      <Text
                        style={[
                          styles.categoryBadgeText,
                          isActive && styles.categoryBadgeTextActive,
                        ]}
                      >
                        {unreadInCategory > 99 ? '99+' : unreadInCategory}
                      </Text>
                    </View>
                  )}
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        </View>

        {error ? (
          <EmptyState
            icon="alert-circle-outline"
            title={t('common.error')}
            message={error?.message || t('notifications.failedToLoad')}
            actionLabel={t('common.retry')}
            onAction={refetch}
          />
        ) : filteredNotifications.length === 0 ? (
          <EmptyState
            icon={currentCategoryConfig?.icon || 'notifications-outline'}
            title={selectedCategory === 'all' ? t('notifications.noNotifications') : t('notifications.noNotificationsInCategory', { category: currentCategoryConfig?.label || '' })}
            message={getEmptyStateMessage()}
          />
        ) : (
          <FlatList
            data={filteredNotifications}
            renderItem={renderItem}
            keyExtractor={(item) => item.id.toString()}
            contentContainerStyle={[styles.list, { padding: containerPadding, minHeight: 500 }]}
            refreshControl={
              <RefreshControl
                refreshing={refreshing}
                onRefresh={onRefresh}
                colors={['#A9211F']}
                tintColor="#A9211F"
              />
            }
            showsVerticalScrollIndicator={false}
          />
        )}
      </PageContainer>

      <ChangeRequestApprovalModal
        visible={showApprovalModal}
        changeRequest={selectedChangeRequest}
        onClose={() => {
          setShowApprovalModal(false);
          setSelectedChangeRequest(null);
        }}
        onApproved={() => { refetch(); }}
        onRejected={() => { refetch(); }}
      />
    </>
  );
}

const styles = StyleSheet.create({
  header: {
    padding: 16,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    alignItems: 'flex-end',
  },
  markAllButton: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: '#FEF2F2',
    borderRadius: 8,
  },
  markAllText: {
    color: '#A9211F',
    fontSize: 14,
    fontFamily: FONTS.medium,
  },
  // Category Filter Tabs styles
  categoryTabsContainer: {
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    paddingVertical: 12,
  },
  categoryTabsScroll: {
    paddingHorizontal: 16,
    gap: 8,
    flexDirection: 'row-reverse',
  },
  categoryTab: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 20,
    backgroundColor: '#F3F4F6',
    gap: 6,
  },
  categoryTabActive: {
    backgroundColor: '#A9211F',
  },
  categoryTabIcon: {
    marginLeft: 2,
  },
  categoryTabText: {
    fontSize: 13,
    fontFamily: FONTS.medium,
    color: '#6B7280',
  },
  categoryTabTextActive: {
    color: '#FFFFFF',
  },
  categoryBadge: {
    backgroundColor: '#EF4444',
    borderRadius: 10,
    minWidth: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 6,
    marginRight: 2,
  },
  categoryBadgeActive: {
    backgroundColor: '#FFFFFF',
  },
  categoryBadgeText: {
    fontSize: 11,
    fontFamily: FONTS.medium,
    color: '#FFFFFF',
    fontWeight: '700',
  },
  categoryBadgeTextActive: {
    color: '#A9211F',
  },
  list: {
    padding: 16,
    paddingBottom: 32,
  },
  card: {
    flexDirection: 'row-reverse',
    alignItems: 'flex-start',
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    marginBottom: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    overflow: 'hidden',
  },
  cardUnread: {
    backgroundColor: '#FFFBEB',
    borderColor: '#FDE68A',
    borderRightWidth: 4,
    borderRightColor: '#A9211F',
  },
  iconContainer: {
    width: 44,
    height: 44,
    borderRadius: 22,
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 12,
  },
  content: {
    flex: 1,
    overflow: 'hidden',
  },
  title: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#374151',
    textAlign: 'right',
    marginBottom: 4,
  },
  titleUnread: {
    fontFamily: FONTS.medium,
    color: '#111827',
  },
  message: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    color: '#6B7280',
    textAlign: 'right',
    lineHeight: 20,
    marginBottom: 8,
  },
  metaRow: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 4,
    overflow: 'hidden',
  },
  orgBadge: {
    backgroundColor: '#FEF2F2',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 4,
  },
  orgText: {
    fontSize: 11,
    fontFamily: FONTS.medium,
    color: '#A9211F',
  },
  time: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#9CA3AF',
  },
  unreadDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#A9211F',
    marginRight: 8,
    marginTop: 4,
  },
  arrow: {
    marginLeft: 8,
    marginTop: 2,
  },
});
